/**
 * High-level state machine for the SHSAT Adaptive test-taking shell.
 *
 * This module tracks the load/answer/advance lifecycle of the whole test
 * page; the per-slot back/forward and lock rules are driven by the
 * server-supplied ``navigability`` flags:
 *
 *      idle  ── START ──▶  fetching ── SLOT_FETCHED ──▶  ready
 *                              │                            │
 *                              └── ERROR ─────────┐         │
 *                                                 ▼         ▼
 *                                              error ◀── ERROR
 *                                                 ▲         │
 *                                                 │         │ SUBMIT_START
 *                                                 │         ▼
 *                                                 │     submitting
 *                                                 │      │   │   │
 *                                                 │      │   │   └── TEST_COMPLETE ─────────▶ complete
 *                                                 │      │   │
 *                                                 │      │   └── SLOT_ADVANCED ──▶ ready (new slot)
 *                                                 └──────┴── ERROR
 *
 * RETRY semantics depend on which side errored: a fetch-error retry
 * re-fetches (currentSlot cleared); a submit-error retry re-submits the
 * SAME slot (currentSlot preserved). The ``errorOrigin`` field records
 * which side errored so RETRY can land in the correct state.
 *
 * Pure: no React, no axios, no side effects. Component wiring lives in
 * ``useAdaptiveFlow`` (coming later) and ``AdaptiveModule.tsx``.
 *
 * Invariant: an event that is not legal in the current state is a no-op
 * (returns the state unchanged), which prevents a stray re-fire from
 * corrupting the state.
 */

export type FlowStatus =
  | 'idle'
  | 'fetching'
  | 'ready'
  | 'submitting'
  | 'complete'
  | 'error';

export type ErrorOrigin = 'fetch' | 'submit';

/**
 * One slot's payload as delivered by the server.
 *
 * NOTE: this DTO is for the student test-taking flow. Tier ('medium' /
 * 'hard') is deliberately NOT included: surfacing the engine's
 * difficulty decision to the client would let a student infer "I just
 * got one wrong" from the response shape. Tutor/review screens that
 * legitimately need the tier should use a different DTO populated from
 * the SHSATAdaptiveServedQuestion audit table server-side.
 */
export interface SlotPayload {
  slotNumber: number;
  passageId: string | null;
  /**
   * Tier-independent marker (e.g. 'REVISING/EDITING PART A') stamped on
   * the slot in the workbook. When set, the player shows a directions
   * interstitial BEFORE this slot's question. Null/absent = no screen.
   */
  sectionDirections?: string | null;
  /**
   * Opaque to this state machine. The shell passes this straight
   * through to QuestionRenderer; the reducer must not inspect it.
   */
  question: unknown;
  /**
   * The student's prior answer (only set when re-serving an already-
   * answered slot via within-passage back navigation). Shape mirrors
   * the submit payload — interpreted by AdaptiveModule keyed on
   * questionType. Null on the first serve of a slot.
   */
  priorAnswer?: { answer: unknown } | null;
  /**
   * Server-computed navigation flags for this slot. ``canGoBack`` is
   * true when the previous slot exists, was served, AND shares the
   * current slot's passage group. ``prevSlotNumber`` is the slot to
   * fetch for the back action (server is the canonical source — the
   * client should NOT compute this arithmetically). ``isLastInPassage``
   * flags the slot whose forward-submit will lock the passage (used to
   * show the "you won't be able to return" confirmation).
   */
  navigability?: {
    canGoBack: boolean;
    prevSlotNumber: number | null;
    isLastInPassage: boolean;
    /**
     * The current passage's reachable (already-served) slots and whether
     * each has an answer. Drives the passage-scoped Review list. Empty
     * for standalone (e.g. Math) slots. Server-supplied — a locked
     * passage's slots are excluded entirely (they can't be re-served).
     */
    passageSlots?: { slotNumber: number; answered: boolean }[];
    /**
     * Passage-set framing so the adaptive top bar / per-set start screen
     * match the legacy player: ``index``/``count`` = this passage set's
     * 1-based position among the module's passage sets and total set
     * count; ``position``/``size`` = the current question's 1-based
     * position within the set and the set's question count. Null for
     * standalone (e.g. Math) slots.
     */
    passageSet?: {
      index: number | null;
      count: number;
      position: number;
      size: number;
    } | null;
    /**
     * ELA "Stand alone items" (Revising/Editing Part B) framing.
     * ``index``/``count`` = this stand-alone block's 1-based position among
     * the module's stand-alone blocks and total block count;
     * ``position``/``size`` = the slot's position within the block and the
     * block length. Null for passage slots and for fully passage-less
     * (e.g. Math) modules.
     */
    /**
     * The ONE unified ELA "Stand alone" block (Revising/Editing Part A +
     * Part B) framing. ``index``/``count`` are always 1/1 (a single block);
     * ``position``/``size`` = the slot's 1-based position within the block and
     * the block's question count — drives the single "Stand alone items"
     * START + top-bar label/count. Set for EVERY slot in the block (incl. Part
     * A's shared-passage slots). Null for reading passages / Math.
     */
    standaloneItems?: {
      index: number;
      count: number;
      position: number;
      size: number;
    } | null;
    /**
     * Size of the current passage GROUP (reading passage OR Part A's shared
     * passage). Gates the per-passage-set Review + Bookmark tools on
     * "multi-question passage set" (> 1). Null for single-question / null /
     * Math slots.
     */
    passageGroupSize?: number | null;
  };
}

export interface AdaptiveFlowState {
  status: FlowStatus;
  currentSlot: SlotPayload | null;
  error: string | null;
  /**
   * Set when entering the ``error`` state; cleared on the next
   * successful transition out. Used by RETRY to decide whether to
   * re-fetch or re-submit.
   */
  errorOrigin: ErrorOrigin | null;
}

export type FlowEvent =
  | { type: 'START' }
  | { type: 'SLOT_FETCHED'; slot: SlotPayload }
  | { type: 'SUBMIT_START' }
  | { type: 'SLOT_ADVANCED'; nextSlot: SlotPayload }
  | { type: 'TEST_COMPLETE' }
  | { type: 'ERROR'; message: string }
  | { type: 'RETRY' }
  /**
   * Merge fresh server navigability into the current slot WITHOUT
   * changing status or re-rendering the question. Used after a
   * persist-only save (advance=false, the end-of-passage interstitial)
   * so the Review list reflects the just-saved answer. No-op unless a
   * slot is currently loaded.
   */
  | { type: 'SLOT_NAVIGABILITY'; navigability: SlotPayload['navigability'] };

export function initState(): AdaptiveFlowState {
  return { status: 'idle', currentSlot: null, error: null, errorOrigin: null };
}

/**
 * Pure reducer. Returns the new state. Illegal transitions return the
 * input state unchanged.
 */
export function transition(
  state: AdaptiveFlowState,
  event: FlowEvent,
): AdaptiveFlowState {
  switch (state.status) {
    case 'idle':
      if (event.type === 'START') {
        return { status: 'fetching', currentSlot: null, error: null, errorOrigin: null };
      }
      return state;

    case 'fetching':
      if (event.type === 'SLOT_FETCHED') {
        return {
          status: 'ready',
          currentSlot: event.slot,
          error: null,
          errorOrigin: null,
        };
      }
      if (event.type === 'TEST_COMPLETE') {
        // Initial serve can return ``moduleComplete: true`` when the
        // module was finished in a prior session and the student is
        // re-entering. Without handling this transition here, the hook
        // would sit in 'fetching' forever and the component would
        // render the "Loading…" stub indefinitely.
        return {
          status: 'complete',
          currentSlot: null,
          error: null,
          errorOrigin: null,
        };
      }
      if (event.type === 'ERROR') {
        return {
          status: 'error',
          currentSlot: null,
          error: event.message,
          errorOrigin: 'fetch',
        };
      }
      return state;

    case 'ready':
      if (event.type === 'SUBMIT_START') {
        return { ...state, status: 'submitting', error: null, errorOrigin: null };
      }
      if (event.type === 'SLOT_NAVIGABILITY') {
        // Refresh navigability on the current slot in place (e.g. after a
        // persist-only save). Pure merge — status and question untouched.
        return state.currentSlot
          ? {
              ...state,
              currentSlot: { ...state.currentSlot, navigability: event.navigability },
            }
          : state;
      }
      if (event.type === 'START') {
        // Within-passage back navigation re-enters ``fetching`` from
        // ``ready``: the caller (useAdaptiveFlow.goBack) abandons the
        // current slot's pending answer and re-serves a different one.
        // Without this branch the dispatch is illegal from ``ready``
        // and the subsequent ``SLOT_FETCHED`` is also illegal — the
        // whole goBack flow silently drops.
        //
        // Preserve ``currentSlot`` so the page keeps rendering the
        // current question with a LoadingPopup overlay (same as the
        // forward-submit flow). Without this, the page blanks out to
        // a full-page "Loading..." stub during the brief fetch — a
        // visible regression from the forward path.
        return { ...state, status: 'fetching', error: null, errorOrigin: null };
      }
      return state;

    case 'submitting':
      if (event.type === 'SLOT_ADVANCED') {
        return {
          status: 'ready',
          currentSlot: event.nextSlot,
          error: null,
          errorOrigin: null,
        };
      }
      if (event.type === 'TEST_COMPLETE') {
        // currentSlot cleared: the completion screen renders a score
        // report, NOT the last question. Carrying the slot would tempt
        // the UI into dereferencing it from a render that no longer
        // expects a question.
        return {
          status: 'complete',
          currentSlot: null,
          error: null,
          errorOrigin: null,
        };
      }
      if (event.type === 'ERROR') {
        // currentSlot preserved so RETRY can re-submit the same slot.
        return {
          status: 'error',
          currentSlot: state.currentSlot,
          error: event.message,
          errorOrigin: 'submit',
        };
      }
      return state;

    case 'error':
      if (event.type === 'RETRY') {
        if (state.errorOrigin === 'submit') {
          // Re-attempt the submit. currentSlot preserved; the hook will
          // re-fire the submit side-effect with the same answer.
          return {
            status: 'submitting',
            currentSlot: state.currentSlot,
            error: null,
            errorOrigin: null,
          };
        }
        // 'fetch' or null (defensive): re-fetch from scratch.
        return { status: 'fetching', currentSlot: null, error: null, errorOrigin: null };
      }
      return state;

    case 'complete':
      // Terminal state. Even RETRY is a no-op here — the caller must
      // mount a fresh state (via initState()) to start over.
      return state;
  }
}

// --- selectors -----------------------------------------------------------

/** True if the UI should ENABLE the Next/Submit button. */
export function canSubmit(state: AdaptiveFlowState): boolean {
  return state.status === 'ready';
}

/** True if the UI should show a loading spinner. */
export function isLoading(state: AdaptiveFlowState): boolean {
  return state.status === 'fetching' || state.status === 'submitting';
}

/** True if the test page should show the score / report card. */
export function isComplete(state: AdaptiveFlowState): boolean {
  return state.status === 'complete';
}

/** True if the UI should show an error banner with a retry button. */
export function hasError(state: AdaptiveFlowState): boolean {
  return state.status === 'error';
}
